import { useMemo } from 'react';
import clsx from 'clsx';

import { useAudioButtonShownEvent } from 'Services/Audio/useAudioButtonShownEvent';
import { useAudioState } from 'Services/Audio/useAudioState';
import { useAudioQueue } from 'Services/Audio/useAudioQueue';
import { isWrapper } from 'Wrapper/criticals';
import { isAudioQueueSupported } from 'Wrapper/supports';
import { useAuthorizedUser } from 'Services/User/useAuthorizedUser';
import { useFocusPlayer } from 'UI/Components/AudioPlayer/useFocusPlayer';
import { PLAYER_BUTTON_CONTENT_PLAY_ID } from 'UI/Components/AudioPlayer/constants';

import { useIsAudioAvailable } from './useIsAudioAvailable';
import { useIsAudioPlaying } from './useIsAudioPlaying';
import { useButtonLockStatus } from './useButtonLockStatus';
import { getAudioUrl } from './getAudioUrl';
import { PlayButton } from './PlayButton';
import { AddToQueueButton } from './AddToQueueButton';
import * as styles from './AudioButton.module.scss';

import type { ServicePlanArticleT } from 'Services/api/types';
import type { FullArticleT } from 'Shared/types/ApiModels';
import type { ArticleTypeT } from 'Constants/articleTypes';

type PropsT = {
  readonly article: FullArticleT | ServicePlanArticleT;
  readonly articleType: ArticleTypeT;
  readonly hideLabel?: boolean;
  // used only for MP articles
  readonly isMpPaidSubscriber?: boolean;
  readonly mpServicePlanId?: string;
};

export const AudioButton = ({
  article,
  articleType,
  hideLabel,
  isMpPaidSubscriber,
  mpServicePlanId,
}: PropsT) => {
  const buttonRef = useAudioButtonShownEvent<HTMLButtonElement>({
    entityId: article.id,
    title: article.title,
  });
  const { url, showPlayer } = useAudioState();
  const isPlaying = useIsAudioPlaying(article.id, articleType);
  const { isLocked } = useButtonLockStatus({
    isProContent: article.isLockedPro,
    isMpPaidSubscriber,
    articleType,
  });
  const isAudioAvailable = useIsAudioAvailable(
    article.audioDuration,
    articleType,
  );
  const { getIsInQueue } = useAudioQueue();
  const { isAnyProSubscriber } = useAuthorizedUser();

  const isInQueue = useMemo(() => {
    return getIsInQueue({
      subjectId: article.id,
      subjectType: articleType,
    });
  }, [article.id, articleType, getIsInQueue]);

  const shouldRenderPlayButton =
    !isAnyProSubscriber ||
    isPlaying ||
    !showPlayer ||
    isLocked ||
    url === getAudioUrl(article.id, articleType, mpServicePlanId) ||
    (!url && !isInQueue);

  const isWrapperNotSupportsQueue = isWrapper && !isAudioQueueSupported;

  const Button =
    shouldRenderPlayButton || isWrapperNotSupportsQueue
      ? PlayButton
      : AddToQueueButton;

  const handleHiddenButtonClick = useFocusPlayer();

  if (!isAudioAvailable) {
    return null;
  }

  return (
    <div
      aria-label="Audio player"
      className="relative flex flex-col items-center"
    >
      <Button
        article={article}
        articleType={articleType}
        buttonRef={buttonRef}
        hideLabel={hideLabel}
        id={PLAYER_BUTTON_CONTENT_PLAY_ID}
        isMpPaidSubscriber={isMpPaidSubscriber}
        mpServicePlanId={mpServicePlanId}
      />

      {showPlayer ? (
        <button
          aria-expanded="true"
          aria-label="Go to Audio Player"
          className={clsx(
            styles.link,
            'h-24 min-w-100',
            'flex items-center justify-center bg-share-card-bg text-medium-r text-share-link',
            'absolute inset-y-0 left-1/2 -translate-x-1/2 -translate-y-full opacity-0',
            'focus:translate-y-0 focus:opacity-100',
          )}
          type="button"
          onClick={handleHiddenButtonClick}
        >
          Go to player
        </button>
      ) : null}
    </div>
  );
};
